import { BuilderState } from '@core/services/state/builder-state';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  OnInit,
  AfterViewInit,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { DayBlockService } from '@app/infrastructure/core/services/api/day-block.service';
import {
  AnswerOption,
  IAnswerOption,
  IDayBlockDTO,
} from '@app/infrastructure/models/day-block';
import { GradeUnitDayDTO } from '@app/infrastructure/models/grade-unit/grade-unit-day';
import {
  IQuizBlockDTO,
  QuizBlockDTO,
  QuizTypes,
} from '@app/infrastructure/models/quiz-block';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FileUploadService } from '@app/infrastructure/core/services/api/file-upload.service';
import {
  FileTypes,
  FileUpload,
  IFileUpload,
} from '@app/infrastructure/models/file';
import { map, mergeMap, switchMap, tap } from 'rxjs/operators';
import {
  BlockState,
  BuilderFacade,
} from '@app/infrastructure/core/services/builder.facade';
import { PageDTO } from '@app/infrastructure/core/services/api/day-page.service';

export interface IOptionFile {
  position: number;
  file: File;
}

@Component({
  selector: 'app-quiz-block',
  templateUrl: './quiz-block.component.html',
  styleUrls: ['./quiz-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuizBlockComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public quiz: IQuizBlockDTO;
  @Input() public page: PageDTO = new PageDTO();
  @Output()
  public blockUpdated: EventEmitter<BlockState> = new EventEmitter();

  @ViewChild(MatStepper) stepper: MatStepper;
  public disabled: boolean = false;
  public QTYPE = QuizTypes;

  public selectedQuizType$: BehaviorSubject<QuizTypes> = new BehaviorSubject(
    null,
  );

  public form: UntypedFormGroup = new UntypedFormGroup({});
  public optionFiles: File[] = [];
  public uploadedImages: any[] = [];
  public imageSavePending: boolean = false;
  public quizState: IQuizBlockDTO = new QuizBlockDTO();
  private subscriptions: Subscription[] = [];
  @Output() emitOptionFile = new EventEmitter<IOptionFile>();
  constructor(
    private dayBlockService: DayBlockService,
    private builderFacade: BuilderFacade,
    private builderState: BuilderState,
    private fileUploadService: FileUploadService,
  ) {}

  public ngOnInit(): void {
    this.quiz ||= new QuizBlockDTO();
    this.selectedQuizType$.next(this.quiz.quizType);
    const subscription = this.builderState.observe().subscribe(() => {
      this.saveBlock();
    });
    this.subscriptions.push(subscription);
  }

  public setFile({ position, file }) {
    this.optionFiles[position] = file;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  public ngAfterViewInit(): void {
    this.determineStep();
  }

  get quizType() {
    return this.selectedQuizType$.getValue();
  }

  set quizType(quizType: QuizTypes) {
    this.selectedQuizType$.next(quizType);
  }

  public changeQuizType(quizType: QuizTypes) {
    this.quizType = quizType;
    this.quiz.quizType = quizType;
    this.quiz.trueOrFalse = null;
    this.quiz.text = '';
    this.quiz.options = null;
  }

  public updateBlockWithImage(formData) {
    this.fileUploadService
      .uploadFiles(formData)
      .pipe(
        switchMap((result) => {
          this.imageSavePending = false;
          const indexes = [];
          this.quiz.options.forEach((item) => {
            if (item.file) indexes.push(item.position);
          });
          result.forEach((res, i) => {
            this.uploadedImages.push(
              new AnswerOption({
                answer: this.quiz.options[i].answer,
                correct: this.quiz.options[i].correct,
                position: indexes[i],
                fileBucket: res.bucket,
                fileId: res.id,
                fileName: res.fileName,
                fileLocation: res.location,
              }),
            );
          });
          return this.dayBlockService.updateDayBlock(
            this.page.gradeUnitDayId,
            this.quiz,
            true,
            this.uploadedImages,
          );
        }),
      )
      .subscribe((quiz: IQuizBlockDTO) => {
        this.quiz = quiz;
        this.quizState = quiz;
        this.uploadedImages = [];
        const blockState = new BlockState(this.quiz);
        this.blockUpdated.emit(blockState);
        this.disabled = false;
      });
  }

  public createBlockWithImage(formData) {
    this.fileUploadService
      .uploadFiles(formData)
      .pipe(
        switchMap((result) => {
          result.forEach((res, i) => {
            this.uploadedImages.push(
              new AnswerOption({
                answer: this.quiz.options[i].answer,
                correct: this.quiz.options[i].correct,
                position: i,
                fileBucket: res.bucket,
                fileId: res.id,
                fileName: res.fileName,
                fileLocation: res.location,
              }),
            );
          });
          this.imageSavePending = false;
          return this.dayBlockService.createDayBlock(
            this.page.gradeUnitDayId,
            this.quiz,
            this.uploadedImages,
          );
        }),
      )
      .subscribe((quiz: IQuizBlockDTO) => {
        this.quiz = quiz;
        this.quizState = quiz;
        const blockState = new BlockState(this.quiz);
        this.blockUpdated.emit(blockState);
        this.disabled = false;
        this.uploadedImages = [];
      });
  }

  public saveBlock(): void {
    this.disabled = true;
    this.buildQuizPayload();
    if (
      this.quiz.quizType === QuizTypes.MULTIPLECHOICE &&
      this.optionFiles.length
    ) {
      this.quiz.options?.forEach((option, i) => {
        if (this.optionFiles[i]) {
          option.file = this.optionFiles[i];
        }
      });
      this.optionFiles = [];
    }
    const hasFile = this.quiz.options?.filter((option) => option.file);
    if (this.quiz.id) {
      if (hasFile?.length) {
        this.imageSavePending = true;
        const formData = new FormData();
        this.quiz.options.map((option, index) => {
          formData.append('files', option.file);
        });
        this.updateBlockWithImage(formData);
      } else {
        this.dayBlockService
          .updateDayBlock(this.page.gradeUnitDayId, this.quiz, true, [])
          .subscribe((quiz: IQuizBlockDTO) => {
            this.quiz = quiz;
            const blockState = new BlockState(this.quiz);
            this.blockUpdated.emit(blockState);
          });
      }
    } else {
      if (hasFile?.length) {
        this.imageSavePending = true;
        const formData = new FormData();
        this.quiz.options.map((option) =>
          formData.append('files', option.file),
        );
        this.createBlockWithImage(formData);
      } else {
        this.dayBlockService
          .createDayBlock(this.page.gradeUnitDayId, this.quiz, [])
          .subscribe((quiz: IQuizBlockDTO) => {
            this.quiz = quiz;
            const blockState = new BlockState(this.quiz);
            this.blockUpdated.emit(blockState);
          });
      }
    }
    this.disabled = false;
  }

  private buildQuizPayload(): void {
    switch (this.quiz.quizType) {
      case QuizTypes.MULTIPLECHOICE:
        this.quiz.options = this.getOptionsFromForm(this.form);
        return;
      case QuizTypes.FREEANSWER:
        this.quiz.text = this.getTextFromAnswerStep(this.form);
        return;
      case QuizTypes.BOOLEANCHOICE:
        this.quiz.trueOrFalse = this.getBoolFromForm(this.form);
        return;
    }
  }

  private getBoolFromForm(fg: UntypedFormGroup): boolean {
    if (fg.get('boolChoice')?.value === '') {
      return null;
    }
    return fg.get('boolChoice')?.value === '1';
  }

  private getTextFromAnswerStep(fg: UntypedFormGroup): string {
    return fg.get('freeanswer')?.value || '';
  }

  private getOptionsFromForm(fg: UntypedFormGroup): IAnswerOption[] {
    const ctrlName = 'answers';
    const answerOptions: IAnswerOption[] = [];
    const formArray = fg.controls[ctrlName] as UntypedFormArray;

    for (let index = 0; index < formArray.controls.length; index++) {
      const castOption = formArray.controls[index].value as {
        id: string;
        label: string;
        correct: boolean;
        answer: string;
        fileId: string;
        fileName: string;
        fileLocation: string;
        fileBucket: string;
      };
      answerOptions.push(
        new AnswerOption({
          answer: castOption.answer,
          position: +index,
          correct: castOption.correct,
          fileId: castOption.fileId,
          fileName: castOption.fileName,
          fileLocation: castOption.fileLocation,
          fileBucket: castOption.fileBucket,
        }),
      );
      this.quizState?.options?.forEach((option, i) => {
        if (answerOptions[i]) {
          answerOptions[i].fileId = option.fileId;
          answerOptions[i].fileName = option.fileName;
          answerOptions[i].fileLocation = option.fileLocation;
          answerOptions[i].fileBucket = option.fileBucket;
        }
      });
    }
    return answerOptions;
  }

  private determineStep() {
    if (this.quiz.id) {
      this.stepper.next();
    }
  }
}
